import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useToast } from "../context/ToastContext";
import api from "../services/api";

const Dashboard = () => {
  const [stats, setStats] = useState({
    new: 0,
    assigned: 0,
    verified: 0,
  });
  const [hospitals, setHospitals] = useState({
    new: [],
    assigned: [],
    verified: [],
  });
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(true);
  const { user, logout } = useAuth();
  const { error } = useToast();
  const navigate = useNavigate();

  useEffect(() => {
    fetchInitialData();
  }, []);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [newHospitals, assignedHospitals, verifiedHospitals] =
        await Promise.all([
          api.getHospitalsByStatus("new"),
          api.getHospitalsByStatus("assigned"),
          api.getHospitalsByStatus("verified"),
        ]);

      setStats({
        new: newHospitals.hospitals?.length || 0,
        assigned: assignedHospitals.hospitals?.length || 0,
        verified: verifiedHospitals.hospitals?.length || 0,
      });

      setHospitals({
        new: newHospitals.hospitals || [],
        assigned: assignedHospitals.hospitals || [],
        verified: verifiedHospitals.hospitals || [],
      });
    } catch (err) {
      error("Failed to fetch hospital statistics");
      console.error("Error fetching stats:", err);
    } finally {
      setLoading(false);
    }
  };

  const fetchHospitalsByStatus = async (status) => {
    try {
      setLoading(true);
      const response = await api.getHospitalsByStatus(status);
      setHospitals((prev) => ({
        ...prev,
        [status]: response.hospitals || [],
      }));
    } catch (err) {
      error(`Failed to fetch ${status} hospitals`);
      console.error(`Error fetching ${status} hospitals:`, err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
  };

  const StatCard = ({ title, count, color, icon }) => (
    <div className={`stat-card stat-card-${color}`}>
      <div className="stat-icon">{icon}</div>
      <div className="stat-content">
        <h3 className="stat-title">{title}</h3>
        <p className="stat-count">{loading ? "..." : count}</p>
      </div>
    </div>
  );

  const updateHospitalStatus = async (hospitalId, newStatus) => {
    try {
      await api.updateHospitalStatus(hospitalId, newStatus);
      // Refresh the data after status update
      await fetchInitialData();
      // Refresh the current tab data
      await fetchHospitalsByStatus(activeTab);
    } catch (err) {
      error("Failed to update hospital status");
      console.error("Error updating status:", err);
    }
  };

  const viewHospitalDetails = (hospitalId) => {
    navigate(`/hospital/${hospitalId}`);
  };

  const HospitalTable = ({ hospitals, status }) => {
    if (!hospitals || hospitals.length === 0) {
      return (
        <div className="no-data">
          <p>No {status} applications found.</p>
        </div>
      );
    }

    const isVerifiedTab = status === "verified";

    return (
      <div className="hospital-table-container">
        <h3>{status.charAt(0).toUpperCase() + status.slice(1)} Applications</h3>
        <table className="hospital-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>City</th>
              <th>Address</th>
              {!isVerifiedTab && <th>Status</th>}
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {hospitals.map((hospital) => (
              <tr key={hospital.id}>
                <td>{hospital.id}</td>
                <td>{hospital.name}</td>
                <td>{hospital.city}</td>
                <td>{hospital.address}</td>
                {!isVerifiedTab && (
                  <td>
                    <select
                      value={hospital.status}
                      onChange={(e) =>
                        updateHospitalStatus(hospital.id, e.target.value)
                      }
                      className="status-select"
                    >
                      <option value="new">New</option>
                      <option value="assigned">Assigned</option>
                      <option value="verified">Verified</option>
                    </select>
                  </td>
                )}
                <td>
                  <button
                    onClick={() => viewHospitalDetails(hospital.id)}
                    className="details-btn"
                  >
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Hospital Dashboard</h1>
          <p>Welcome {user?.username}!</p>
        </div>
        <button onClick={handleLogout} className="logout-btn">
          Logout
        </button>
      </div>

      <div className="dashboard-content">
        <div className="stats-grid">
          <div
            className={`stat-card stat-card-blue ${
              activeTab === "new" ? "active" : ""
            }`}
            onClick={() => setActiveTab("new")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon">📋</div>
            <div className="stat-content">
              <h3 className="stat-title">New Applications</h3>
              <p className="stat-count">{loading ? "..." : stats.new}</p>
            </div>
          </div>
          <div
            className={`stat-card stat-card-orange ${
              activeTab === "assigned" ? "active" : ""
            }`}
            onClick={() => setActiveTab("assigned")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon">👥</div>
            <div className="stat-content">
              <h3 className="stat-title">Assigned</h3>
              <p className="stat-count">{loading ? "..." : stats.assigned}</p>
            </div>
          </div>
          <div
            className={`stat-card stat-card-green ${
              activeTab === "verified" ? "active" : ""
            }`}
            onClick={() => setActiveTab("verified")}
            style={{ cursor: "pointer" }}
          >
            <div className="stat-icon">✅</div>
            <div className="stat-content">
              <h3 className="stat-title">Verified</h3>
              <p className="stat-count">{loading ? "..." : stats.verified}</p>
            </div>
          </div>
        </div>

        <div className="dashboard-actions">
          <button
            onClick={() => fetchHospitalsByStatus(activeTab)}
            className="refresh-btn"
            disabled={loading}
          >
            {loading ? "Refreshing..." : "Refresh Data"}
          </button>
        </div>

        {activeTab && (
          <div className="table-section">
            {activeTab === "new" && (
              <HospitalTable hospitals={hospitals.new} status="new" />
            )}
            {activeTab === "assigned" && (
              <HospitalTable hospitals={hospitals.assigned} status="assigned" />
            )}
            {activeTab === "verified" && (
              <HospitalTable hospitals={hospitals.verified} status="verified" />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
